import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Table, Divider, Typography } from 'antd';
import { uuid } from "../../utils/uuid";
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

import client from "../../utils/http-request";
import {formatRate} from "../../utils/roeUtils";

const defaultPageSize = 100;
const defaultPage = 1;

interface DataType {
    id: string
    currency: string
    rate: string
    effectiveYearMonth: string
    lastUpdatedTime: String
}


interface TableParams {
    pagination?: TablePaginationConfig;
}

const buildColum = (currencyCode:String = 'USD') => {
    return [
        {
            title: 'Currency',
            dataIndex: 'currency'
        },
        {
            title: 'Current Currency to ' + currencyCode,
            dataIndex: 'rate',
            render: formatRate
        },
        {
            title: 'Effective Year Month',
            dataIndex: 'effectiveYearMonth'
        },
        {
            title: 'Last Update Datetime (GMT)',
            dataIndex: 'lastUpdatedTime'
        }
    ];
}


const SearchMonthlyResult: React.FC<any> = ({ searchCriteria }) => {

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: defaultPageSize,
            showSizeChanger: false
        },
    });
    const [columns, setColumns] = useState(buildColum());

    const buildSearchResult = async (page: number) => {
        if (!searchCriteria) {
            return;
        }

        setColumns(buildColum(searchCriteria.toCurrency));

        const params = {
            page: page - 1,
            size: defaultPageSize
        }
        const data = {
            startDate: searchCriteria.period?.[0].format('YYYYMM'),
            endDate: searchCriteria.period?.[1].format('YYYYMM'),
            fromCurrency: searchCriteria.fromCurrency,
            toCurrency: searchCriteria.toCurrency
        }
        setLoading(true);
        const res: any = await client.request({
            method: 'post',
            url: '/gateway/gpd_roe_profile_route/roe/monthly',
            data,
            params
        });

        if (res?.status === 200) {
            const responseData = res.data;
            const totalCount = responseData.totalElements;
            let displayData: any[] = [];
            if (responseData.content) {
                displayData = (responseData.content as any[]).map(e => ({
                    id: uuid(),
                    currency: e.fromCurrency,
                    rate: e.rate,
                    effectiveYearMonth: dayjs(e.effectiveYearMonth).format('MMM YYYY'),
                    lastUpdatedTime: dayjs(e.lastUpdatedTime).format('YYYY-MM-DD HH:mm:ss')
                }))
            }
            setData([...displayData]);
            setTableParams({
                ...tableParams,
                pagination: {
                    current: page,
                    pageSize: defaultPageSize,
                    total: totalCount,
                    showTotal: (total, range) => `Total ${total} items and current range from ${range[0]} to ${range[1]}`,
                    showSizeChanger: false
                },
            });
        }
        setLoading(false);

    };

    const handleTableChange = (
        pagination: TablePaginationConfig,
    ) => {
        setTableParams({
            pagination,
        });

        if (pagination.pageSize !== tableParams.pagination?.pageSize) {
            setData([]);
        }
        buildSearchResult(pagination?.current ?? defaultPage);
    };

    useEffect(() => {
        buildSearchResult(defaultPage);
    }, [searchCriteria]);

    return (
        <>
            <div style={{ background: 'white', padding: '0 16px 16px 16px', marginTop: '0px' }}>
                <Typography.Text strong style={{ fontSize: '14px' }}>Result</Typography.Text>
                <Divider style={{ borderBlockStartColor: 'rgba(0, 0, 0, 0.7)', margin: '16px 0' }} />
                <Table
                    rowKey={(record) => record.id}
                    dataSource={data}
                    columns={columns}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={handleTableChange}
                    bordered
                    scroll={{ y: 450 }}
                    size={'middle'}
                />
            </div>
        </>
    )
}

export default SearchMonthlyResult
